import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Sparkles, Wand2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const DIRECTION_PRESETS = [
  '整組改狠一點，用更有衝擊的 hook',
  '換成針對寶媽／職場媽媽',
  '多幾個痛點拆解 DNA',
  '全部改成問句型 hook',
  '再溫柔一點，不要戳太痛',
  '多用數字錨定類公式 C18-C22',
];

/**
 * Regenerate Modal (dumb UI).
 * All SSE logic lives in parent <Carousel/> via `onSubmit(text)`.
 *
 * Behavior:
 *  - User enters supplement (or clicks preset, or clicks 跳過直接重生).
 *  - Parent runs SSE. If AI clarifies, parent pushes `streamingText` prop → we show in-modal.
 *    If AI regenerates, parent closes the modal (regenOpen=false) and shows streaming topics
 *    on the main Screen 2.
 */
export const TopicsRegenerateModal = ({
  open,
  onClose,
  history = [], // [{role, content}]
  streamingText = '',
  sending = false,
  credits = 0,
  suggestions = [],
  suggestionsLoading = false,
  onSubmit, // (text) => void
  onDirectRegenerate, // () => void
}) => {
  const [input, setInput] = useState('');

  useEffect(() => {
    if (open) setInput('');
  }, [open]);

  const handleSubmit = (override) => {
    const t = (override ?? input).trim();
    if (!t || sending) return;
    setInput('');
    onSubmit(t);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          data-testid="carousel-regen-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !sending) onClose();
          }}
        >
          <motion.div
            initial={{ scale: 0.96, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 12 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-xl bg-obsidian-900 border border-obsidian-700 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-obsidian-700">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-lime-400" />
                <span className="text-base font-semibold text-white">重新生成選題</span>
                {credits > 0 && (
                  <span className="ml-2 text-[10px] font-medium text-lime-400 bg-lime-500/10 border border-lime-500/30 px-2 py-0.5 rounded-full">
                    {credits.toFixed(2)} 點數
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="text-obsidian-400 hover:text-white disabled:opacity-40 p-1 rounded"
                data-testid="carousel-regen-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              {history.length === 0 && !sending && (
                <>
                  <div className="flex items-start gap-2 text-[12.5px] text-obsidian-300 leading-relaxed mb-4">
                    <Sparkles className="w-3.5 h-3.5 text-lime-400 mt-0.5 flex-shrink-0" />
                    <span>
                      有沒有想特別補充的方向？AI 會根據你的補充重新生成 10 題，並會避開前幾輪已經出過的題目。
                      若補充內容夠清楚會直接生成，不夠清楚會先追問一次。
                    </span>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-obsidian-500 font-semibold mb-1.5 flex items-center gap-2">
                    <span>方向快選</span>
                    {suggestions.length > 0 && (
                      <span className="text-[9px] text-lime-400 bg-lime-500/10 border border-lime-500/30 px-1.5 py-0.5 rounded-full normal-case tracking-normal">
                        依據你的定位產生
                      </span>
                    )}
                    {suggestionsLoading && (
                      <Loader2 className="w-3 h-3 animate-spin text-obsidian-500" />
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-1.5 mb-4">
                    {(suggestions.length > 0 ? suggestions : DIRECTION_PRESETS).map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`carousel-regen-preset-${s}`}
                        onClick={() => handleSubmit(s)}
                        disabled={sending}
                        className="text-left text-[12px] text-obsidian-200 hover:text-lime-300 bg-obsidian-950/70 hover:bg-lime-500/5 border border-obsidian-700 hover:border-lime-500/40 rounded-md px-3 py-2 transition-colors disabled:opacity-40 leading-snug"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {history.length > 0 && (
                <div className="space-y-3 mb-4">
                  {history.map((m, i) => (
                    <div
                      key={i}
                      className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] text-[12.5px] leading-relaxed rounded-lg px-3 py-2 whitespace-pre-wrap ${
                          m.role === 'user'
                            ? 'bg-lime-500/10 border border-lime-500/30 text-lime-50'
                            : 'bg-obsidian-950/70 border border-obsidian-700 text-obsidian-100'
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {sending && (
                <div className="flex justify-start" data-testid="carousel-regen-streaming">
                  <div className="max-w-[85%] text-[12.5px] leading-relaxed rounded-lg px-3 py-2 whitespace-pre-wrap bg-obsidian-950/70 border border-obsidian-700 text-obsidian-100">
                    {streamingText || (
                      <span className="inline-flex items-center gap-1 text-obsidian-400 text-[11px]">
                        <Loader2 className="w-3 h-3 animate-spin" /> 思考中…
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="border-t border-obsidian-700 px-5 py-3 bg-obsidian-950/60"
            >
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="carousel-regen-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  placeholder={
                    history.length > 0 && !sending
                      ? '回覆 AI 的追問…'
                      : sending
                        ? '生成中…'
                        : '例：再狠一點、換個角度、針對寶媽…'
                  }
                  rows={1}
                  disabled={sending}
                  className="flex-1 resize-none bg-obsidian-900 border border-obsidian-600 focus:border-lime-500 rounded-md px-3 py-2 text-[13px] text-white placeholder-obsidian-500 outline-none max-h-32"
                />
                <button
                  type="submit"
                  data-testid="carousel-regen-send"
                  disabled={sending || !input.trim()}
                  className="flex-shrink-0 bg-lime-500 hover:bg-lime-400 disabled:bg-obsidian-700 disabled:text-obsidian-500 text-obsidian-900 rounded-md h-9 px-3 flex items-center gap-1.5 transition-colors text-[12px] font-semibold"
                >
                  {sending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="w-3.5 h-3.5" />
                  )}
                  送出
                </button>
              </div>
              {history.length === 0 && !sending && onDirectRegenerate && (
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      onDirectRegenerate();
                      onClose();
                    }}
                    data-testid="carousel-regen-direct"
                    className="text-[11px] text-obsidian-400 hover:text-white underline-offset-2 hover:underline"
                  >
                    跳過補充，直接重新生成
                  </button>
                </div>
              )}
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TopicsRegenerateModal;
